import { shopeeVideoDomains } from 'utils/constants';
import posthog from '@leyan/lytics';

/**
 * 替换 URL 中的域名为指定的备份域名
 */
const replaceDomain = (url: string, newDomain: string): string => {
  try {
    const urlObj = new URL(url);
    urlObj.hostname = newDomain;
    return urlObj.toString();
  } catch {
    // 如果 URL 解析失败，尝试简单的字符串替换
    const domainMatch = url.match(/https?:\/\/([^/]+)/);
    if (domainMatch) {
      return url.replace(domainMatch[1], newDomain);
    }
    return url;
  }
};

/**
 * 从 URL 中提取当前域名
 */
const extractDomain = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    const domainMatch = url.match(/https?:\/\/([^/]+)/);
    return domainMatch ? domainMatch[1] : null;
  }
};

/**
 * 域名状态接口
 */
interface DomainStatus {
  /** 不可用的域名集合 */
  unavailableDomains: Set<string>;
  /** 最快可用域名映射（域名 -> 最快可用域名） */
  fastestDomainMap: Map<string, string>;
  /** 域名测试结果缓存（域名 -> 响应时间） */
  domainResponseTimeMap: Map<string, number>;
}

/**
 * 域名灾备服务类
 * 负责管理域名灾备切换逻辑
 */
class DomainBackupService {
  /** 域名状态缓存，在 APP 运行内存内生效 */
  private domainStatus: DomainStatus = {
    unavailableDomains: new Set(),
    fastestDomainMap: new Map(),
    domainResponseTimeMap: new Map(),
  };

  /** 正在进行的域名测试请求控制器 */
  private abortController: AbortController | null = null;

  private isTesting: boolean = false;

  private readonly _domains: string[];

  constructor(domains: string[]) {
    this._domains = domains;
  }

  /**
   * 测试单个域名的可访问性和响应速度
   */
  private async testDomainAccessibility(
    testUrl: string,
    domain: string,
    signal: AbortSignal,
  ): Promise<{ domain: string; url: string; responseTime: number } | null> {
    const startTime = performance.now();
    try {
      // 使用 HEAD 请求测试域名响应速度
      const response = await fetch(testUrl, {
        method: 'HEAD',
        signal,
        cache: 'no-cache',
      });

      const responseTime = performance.now() - startTime;

      // 检查响应状态，2xx 和 3xx 都认为是可以访问的
      if (response.ok || (response.status >= 300 && response.status < 400)) {
        // 缓存响应时间
        this.domainStatus.domainResponseTimeMap.set(domain, responseTime);
        return { domain, url: testUrl, responseTime };
      }
      return null;
    } catch (error) {
      // 如果是主动取消，不记录错误
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('AbortError', error);
        return null;
      }
      // 其他错误表示域名不可访问
      return null;
    }
  }

  /**
   * 通过赛马机制寻找最快可访问的备份域名
   * @param currentUrl 当前视频 URL
   * @returns 返回最快响应的 URL，如果都失败则返回 null
   */
  async findFastestBackupDomain(currentUrl: string): Promise<string | null> {
    if (!currentUrl) {
      return null;
    }

    const currentDomain = extractDomain(currentUrl);
    if (!currentDomain) {
      return null;
    }

    // 检查当前域名是否已被标记为不可用
    if (this.domainStatus.unavailableDomains.has(currentDomain)) {
      // 如果当前域名不可用，检查是否有缓存的最快可用域名
      const cachedFastestDomain = this.domainStatus.fastestDomainMap.get(currentDomain);
      if (cachedFastestDomain) {
        // 检查缓存的最快域名是否仍然可用（未被标记为不可用）
        if (!this.domainStatus.unavailableDomains.has(cachedFastestDomain)) {
          // 使用缓存的最快域名，直接替换当前 URL 的域名
          const cachedUrl = replaceDomain(currentUrl, cachedFastestDomain);
          return cachedUrl;
        } else {
          // 缓存的最快域名也已失效，清除该缓存并重新检测
          this.domainStatus.fastestDomainMap.delete(currentDomain);
        }
      }
    }

    // 获取所有未标记为不可用的备份域名
    const availableDomains = this._domains.filter(
      (domain) => !this.domainStatus.unavailableDomains.has(domain) && domain !== currentDomain,
    );

    if (availableDomains.length === 0) {
      // 如果所有域名都不可用，清除缓存并重新测试所有域名
      this.domainStatus.unavailableDomains.clear();
      this.domainStatus.fastestDomainMap.clear();
      // 重新获取所有域名（排除当前域名）
      const allDomains = this._domains.filter((domain) => domain !== currentDomain);
      if (allDomains.length === 0) {
        return null;
      }
      const result = await this.testAllDomains(currentUrl, allDomains);
      posthog.capture('$domainBackupDomainFastest', {
        domain: result || '',
        reset: true,
      });
      return result;
    }

    // 测试可用的域名
    const result = await this.testAllDomains(currentUrl, availableDomains);
    posthog.capture('$domainBackupDomainFastest', {
      domain: result || '',
      reset: false,
    });
    return result;
  }

  /**
   * 测试所有域名并选择最快的
   */
  private async testAllDomains(
    currentUrl: string,
    domainsToTest: string[],
  ): Promise<string | null> {
    if (this.isTesting) {
      return null;
    }

    const currentDomain = extractDomain(currentUrl);
    if (!currentDomain) {
      return null;
    }

    // 取消之前的测试请求
    if (this.abortController) {
      this.abortController.abort();
    }

    // 创建新的 AbortController 用于取消请求
    this.abortController = new AbortController();

    // 为每个域名生成测试 URL
    const testUrls = domainsToTest.map((domain) => ({
      domain,
      url: replaceDomain(currentUrl, domain),
    }));

    // 并行测试所有域名
    const testPromises = testUrls.map(({ domain, url }) =>
      this.testDomainAccessibility(url, domain, this.abortController!.signal),
    );

    // 创建一个超时定时器，3秒后取消所有请求
    const timeoutId = setTimeout(() => {
      if (this.abortController) {
        this.abortController.abort();
      }
    }, 3000);

    try {
      // 等待所有测试完成
      this.isTesting = true;
      const results = await Promise.allSettled(testPromises);

      // 清除超时定时器
      clearTimeout(timeoutId);

      // 处理测试结果
      const successfulResults = results
        .filter(
          (
            result,
          ): result is PromiseFulfilledResult<{
            domain: string;
            url: string;
            responseTime: number;
          }> => result.status === 'fulfilled' && result.value !== null,
        )
        .map((result) => result.value);

      if (successfulResults.length > 0) {
        // 选择响应时间最短的域名
        const fastest = successfulResults.reduce((prev, current) =>
          current.responseTime < prev.responseTime ? current : prev,
        );

        // 标记当前域名为不可用（如果当前域名在备份列表中）
        if (this._domains.includes(currentDomain)) {
          this.domainStatus.unavailableDomains.add(currentDomain);
          // 缓存最快可用域名
          this.domainStatus.fastestDomainMap.set(currentDomain, fastest.domain);
        }

        // 取消其他请求
        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }

        return fastest.url;
      }

      // 如果所有测试都失败，标记所有测试的域名为不可用
      domainsToTest.forEach((domain) => {
        this.domainStatus.unavailableDomains.add(domain);
      });

      // 如果当前域名在备份列表中，也标记为不可用
      if (this._domains.includes(currentDomain)) {
        this.domainStatus.unavailableDomains.add(currentDomain);
      }

      return null;
    } catch (error) {
      // 清除超时定时器
      clearTimeout(timeoutId);
      // 如果被取消，返回 null
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }
      return null;
    } finally {
      this.isTesting = false;
    }
  }

  /**
   * 标记域名为不可用（当视频播放失败时调用）
   */
  markDomainAsUnavailable(url: string): void {
    const domain = extractDomain(url);
    if (domain && !this.domainStatus.unavailableDomains.has(domain)) {
      posthog.capture('$domainBackupDomainUnavailable', {
        domain,
      });
      this.domainStatus.unavailableDomains.add(domain);
      // 清除所有以该域名为最快域名的缓存映射（因为该域名已失效）
      const domainsToRemove: string[] = [];
      this.domainStatus.fastestDomainMap.forEach((fastestDomain, originalDomain) => {
        if (fastestDomain === domain) {
          domainsToRemove.push(originalDomain);
        }
      });
      domainsToRemove.forEach((originalDomain) => {
        this.domainStatus.fastestDomainMap.delete(originalDomain);
      });
      // 也清除该域名作为原始域名的缓存
      this.domainStatus.fastestDomainMap.delete(domain);
    }
  }

  /**
   * 清除所有缓存（用于重置状态）
   */
  clearCache(): void {
    this.domainStatus.unavailableDomains.clear();
    this.domainStatus.fastestDomainMap.clear();
    this.domainStatus.domainResponseTimeMap.clear();
  }

  /**
   * 获取域名状态（用于调试）
   */
  getDomainStatus(): DomainStatus {
    return {
      unavailableDomains: new Set(this.domainStatus.unavailableDomains),
      fastestDomainMap: new Map(this.domainStatus.fastestDomainMap),
      domainResponseTimeMap: new Map(this.domainStatus.domainResponseTimeMap),
    };
  }
}

// 导出shopee video灾备服务单例实例
export const shopeeVideoBackupService = new DomainBackupService(shopeeVideoDomains);
